import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { LoginService } from '../login.service';
import {
  DiagnosisCatalogDto,
  DiagnosisEntry,
  DiagnosisNode,
  ExamDiagnosis,
} from './exam-diagnoses.model';

const byLabel = (a: DiagnosisNode, b: DiagnosisNode) => a.label.localeCompare(b.label);

interface CategoryRow {
  id: number;
  parentId: number | null;
  label: string;
  kids: number[];
  itemIds: number[];
}

/**
 * Holds the shared diagnoses catalogue and persists what was picked on an exam.
 *
 * The catalogue is fetched once per login - one request, gzipped by the API - and
 * kept in memory for the session. It used to be two ~1.8 MB JSON assets fetched
 * from /assets on the first keystroke in every session, which stalled that
 * keystroke because the API serves static files without compression.
 *
 * Searching and drilling the tree are both pure in-memory work after that, so
 * neither costs a round trip.
 */
@Injectable({
  providedIn: 'root',
})
export class ExamDiagnosesService {
  /** null until the catalogue has arrived; the component waits on ready(). */
  private entries: DiagnosisEntry[] = null;
  private byId = new Map<number, DiagnosisEntry>();
  private cats = new Map<number, CategoryRow>();
  private rootIds: number[] = [];
  /** built on the first tree open, from the categories already in memory */
  private tree: DiagnosisNode[] = null;

  private state$ = new BehaviorSubject<'idle' | 'loading' | 'ready' | 'error'>('idle');

  constructor(private http: HttpClient, private login: LoginService) {
    // Same pattern as the other post-login loads (ExamsService.getCustomTitles).
    this.login.onLogin.subscribe(() => this.loadCatalog());
  }

  get isReady(): boolean {
    return this.state$.value === 'ready';
  }

  /** Emits once the catalogue is in memory. Triggers the load if login missed it. */
  ready(): Observable<boolean> {
    if (this.isReady) return of(true);
    if (this.state$.value === 'idle' || this.state$.value === 'error') this.loadCatalog();
    return this.state$.pipe(
      filter((s) => s === 'ready' || s === 'error'),
      take(1),
      map((s) => s === 'ready')
    );
  }

  loadCatalog() {
    if (this.state$.value === 'loading' || this.state$.value === 'ready') return;
    this.state$.next('loading');
    try {
      this.http.get<DiagnosisCatalogDto>('/api/diagnoses/catalog').subscribe(
        (dto) => {
          try {
            this.index(dto);
            this.state$.next('ready');
          } catch (e) {
            console.log(e);
            this.state$.next('error');
          }
        },
        (err) => {
          console.log(err);
          this.state$.next('error');
        }
      );
    } catch (e) {
      console.log(e);
      this.state$.next('error');
    }
  }

  /** Turns the three flat arrays into the shapes search and the tree need. */
  private index(dto: DiagnosisCatalogDto) {
    this.cats.clear();
    this.byId.clear();
    this.rootIds = [];
    this.tree = null;

    for (const [id, parentId, label] of dto.categories || []) {
      this.cats.set(id, { id, parentId, label, kids: [], itemIds: [] });
    }
    for (const c of this.cats.values()) {
      if (c.parentId && this.cats.has(c.parentId)) this.cats.get(c.parentId).kids.push(c.id);
      else this.rootIds.push(c.id);
    }

    this.entries = (dto.diagnoses || []).map(([id, c, n]) => {
      const e: DiagnosisEntry = { id, n, c: c || null, p: null, _l: (n || '').toLowerCase() };
      this.byId.set(id, e);
      return e;
    });

    for (const [catId, diagId] of dto.items || []) {
      const cat = this.cats.get(catId);
      if (cat) cat.itemIds.push(diagId);
      // First membership wins as the label shown next to a search hit. Items arrive
      // ordered by category, so this is stable across loads.
      const e = this.byId.get(diagId);
      if (e && e.p === null && cat) e.p = this.pathOf(catId);
    }
  }

  private pathOf(catId: number): string {
    const parts: string[] = [];
    let c = this.cats.get(catId);
    let guard = 0;
    while (c && guard++ < 20) {
      parts.unshift(c.label);
      c = c.parentId ? this.cats.get(c.parentId) : null;
    }
    return parts.join(' > ');
  }

  /**
   * Filters on every keystroke, in memory. Names that start with the term come
   * first, then names that merely contain it, so typing "mel" surfaces "Melanoma"
   * above "Malignant melanoma".
   */
  filter(term: string, limit = 50): DiagnosisEntry[] {
    const t = (term || '').trim().toLowerCase();
    if (!t || !this.entries) return [];
    const starts: DiagnosisEntry[] = [];
    const contains: DiagnosisEntry[] = [];
    for (const e of this.entries) {
      if (e._l.startsWith(t)) {
        if (starts.length < limit) starts.push(e);
      } else if (contains.length < limit && e._l.indexOf(t) > -1) {
        contains.push(e);
      }
      if (starts.length >= limit) break;
    }
    return starts.concat(contains).slice(0, limit);
  }

  /** The 62 departments. */
  getRoots(): DiagnosisNode[] {
    if (!this.entries) return [];
    if (!this.tree) this.tree = this.rootIds.map((id) => this.catNode(id)).sort(byLabel);
    return this.tree;
  }

  /**
   * The level below a category: sub-categories first, then the diagnoses filed
   * directly under it, each group alphabetical.
   */
  childrenOf(node: DiagnosisNode): DiagnosisNode[] {
    const cat = node && node.catId ? this.cats.get(node.catId) : null;
    if (!cat) return [];
    const subs = cat.kids.map((k) => this.catNode(k)).sort(byLabel);
    const leaves = cat.itemIds
      .map((i) => this.byId.get(i))
      .filter((e) => !!e)
      .map((e) => ({ label: e.n, entry: e, hasChildren: false }))
      .sort(byLabel);
    return subs.concat(leaves);
  }

  private catNode(catId: number): DiagnosisNode {
    const cat = this.cats.get(catId);
    return {
      label: cat ? cat.label : '',
      catId,
      hasChildren: !!cat && (cat.kids.length > 0 || cat.itemIds.length > 0),
    };
  }

  // ---- per-exam storage ----

  get(examID: string | number) {
    return this.http.get(`/api/exam-diagnoses/${examID}`);
  }
  save(examID: string | number, diagnoses: ExamDiagnosis[]) {
    return this.http.put(`/api/exam-diagnoses/${examID}`, { diagnoses });
  }
  saveNotes(examID: string | number, Notes: string) {
    return this.http.post(`/api/exam-diagnoses/${examID}/notes`, { Notes });
  }
}
